import type {
  CommunityPreviewDeviceMeta,
  CustomLightingData,
  CustomLightingInfo,
} from "@eweadn-qiankun/shared-types";
import type { LightingPreviewParams } from "./boardConfigPreviewUtils";
import type { TFunction } from "i18next";
import { shouldHideHfdHeartbeatLighting } from "@eweadn-qiankun/shared-utils";
import {
  buildRongyuanDecorateLightingUiOptions,
  buildRongyuanLightingUiOptions,
  isRongyuanDecorateOffUiMode,
  RONGYUAN_DECORATE_AP_RGB,
  RONGYUAN_DECORATE_STRIP_LED_COUNT,
  supportsRongyuanDecorate1Lighting,
} from "@eweadn-qiankun/shared-utils";
import {
  huafendaLightingBoxData,
  huafendaLightingData,
} from "../../../../../keyboard-management/src/pages/lighting/huafendaLightingData";
import {
  XINGSHAN_DEFAULT_STRIP_MODE_LABEL_KEYS,
  XINGSHAN_DK68_FAMILY_STRIP_MODE_LABEL_KEYS,
  getXingshanDecorativeStripCols,
  isXingshanDk68FamilyStripKeyboardEquipment,
} from "../../../utils/es87LightingUtils";

export interface LightingModeOption {
  label: string;
  value: number;
}

interface ModeDefinition {
  labelKey: string;
  value: number;
}

export const STATIC_EFFECT_PALETTE_COLORS = [
  "",
  "#FF3D3D",
  "#FFD42B",
  "#2EE944",
  "#00D8E8",
  "#2558FF",
  "#E83FD7",
  "#E8E8E8",
];

const XINGSHAN_KEYBOARD_MODES: ModeDefinition[] = [
  { labelKey: "keyboard.lighting.modes.static", value: 0 },
  { labelKey: "keyboard.lighting.modes.wave", value: 1 },
  { labelKey: "keyboard.lighting.modes.tide", value: 2 },
  { labelKey: "keyboard.lighting.modes.ripple", value: 3 },
  { labelKey: "keyboard.lighting.modes.storm", value: 4 },
  { labelKey: "keyboard.lighting.modes.rainbow", value: 5 },
  { labelKey: "keyboard.lighting.modes.swirl", value: 6 },
  { labelKey: "keyboard.lighting.modes.sparkle", value: 7 },
  { labelKey: "keyboard.lighting.modes.glow", value: 8 },
  { labelKey: "keyboard.lighting.modes.pane", value: 9 },
  { labelKey: "keyboard.lighting.modes.flow", value: 10 },
  { labelKey: "keyboard.lighting.modes.sine", value: 11 },
  { labelKey: "keyboard.lighting.modes.mosaic", value: 12 },
  { labelKey: "keyboard.lighting.modes.blossom", value: 13 },
  { labelKey: "keyboard.lighting.modes.rain", value: 14 },
  { labelKey: "keyboard.lighting.modes.morph", value: 15 },
  { labelKey: "keyboard.lighting.modes.snow", value: 17 },
  { labelKey: "keyboard.lighting.modes.laser", value: 18 },
  { labelKey: "keyboard.lighting.modes.waterRipple", value: 19 },
];

const XINGSHAN_STRIP_MODES: ModeDefinition[] =
  XINGSHAN_DEFAULT_STRIP_MODE_LABEL_KEYS.map((labelKey, index) => ({
    labelKey,
    value: index,
  }));

function buildXingshanStripModeDefinitions(
  displayname?: string
): ModeDefinition[] {
  if (
    isXingshanDk68FamilyStripKeyboardEquipment({
      solutionRovider: "xingshan",
      displayname,
    })
  ) {
    return XINGSHAN_DK68_FAMILY_STRIP_MODE_LABEL_KEYS.map(
      (labelKey, index) => ({
        labelKey,
        value: index,
      })
    );
  }
  return XINGSHAN_STRIP_MODES;
}

const BEIYING_KEYBOARD_MODES: ModeDefinition[] = [
  { labelKey: "keyboard.lighting.beiying.off", value: 0 },
  { labelKey: "keyboard.lighting.beiying.static", value: 1 },
  { labelKey: "keyboard.lighting.beiying.breathing", value: 2 },
  { labelKey: "keyboard.lighting.beiying.dreamRainbow", value: 3 },
  { labelKey: "keyboard.lighting.beiying.instant", value: 4 },
  { labelKey: "keyboard.lighting.beiying.rainWalk", value: 5 },
  { labelKey: "keyboard.lighting.beiying.rainbowWheel", value: 6 },
  { labelKey: "keyboard.lighting.beiying.ripple", value: 7 },
  { labelKey: "keyboard.lighting.beiying.starry", value: 8 },
  { labelKey: "keyboard.lighting.beiying.snowTrail", value: 9 },
  { labelKey: "keyboard.lighting.beiying.flow", value: 10 },
  { labelKey: "keyboard.lighting.beiying.wave", value: 11 },
  { labelKey: "keyboard.lighting.beiying.shadow", value: 12 },
  { labelKey: "keyboard.lighting.beiying.lightWave", value: 13 },
  { labelKey: "keyboard.lighting.beiying.scan", value: 14 },
  { labelKey: "keyboard.lighting.beiying.windmill", value: 15 },
  { labelKey: "keyboard.lighting.beiying.waterfall", value: 16 },
  { labelKey: "keyboard.lighting.beiying.blossom", value: 17 },
  { labelKey: "keyboard.lighting.beiying.storm", value: 18 },
  { labelKey: "keyboard.lighting.beiying.custom", value: 19 },
];

const BEIYING_STRIP_MODES: ModeDefinition[] = [
  { labelKey: "keyboard.lighting.beiying.off", value: 0 },
  { labelKey: "keyboard.lighting.beiying.flow", value: 1 },
  { labelKey: "keyboard.lighting.beiying.dreamRainbow", value: 2 },
  { labelKey: "keyboard.lighting.beiying.steady", value: 3 },
  { labelKey: "keyboard.lighting.beiying.breathing", value: 4 },
  { labelKey: "keyboard.lighting.beiying.runningHorse", value: 5 },
];

function mapModeDefinitions(
  definitions: ModeDefinition[],
  t: TFunction
): LightingModeOption[] {
  return definitions.map((item) => ({
    label: t(item.labelKey),
    value: item.value,
  }));
}

/** 社区预览：华奋达设备上下文（对齐 shouldHideHfdHeartbeatLighting） */
export type HuafendaLightingPreviewEquipment = {
  displayname?: string;
  keyboardType?: string;
  identifier?: string;
};

export function getLightingModeOptions(
  solutionProvider: string,
  area: "Keyboard" | "Decorate1",
  t: TFunction,
  hfdEquipment?: HuafendaLightingPreviewEquipment
): LightingModeOption[] {
  const provider = solutionProvider.toLowerCase();

  if (provider === "huafenda") {
    const source =
      area === "Decorate1" ? huafendaLightingBoxData : huafendaLightingData;
    let options = source.map((item) => ({
      label: t(item.labelKey),
      value: item.value,
    }));
    if (
      area === "Keyboard" &&
      hfdEquipment &&
      shouldHideHfdHeartbeatLighting({
        solutionRovider: "huafenda",
        displayname: hfdEquipment.displayname,
        keyboardType: hfdEquipment.keyboardType,
        identifier: hfdEquipment.identifier,
      })
    ) {
      options = options.filter((item) => item.value !== 254);
    }
    return options;
  }

  if (provider === "beiying") {
    return mapModeDefinitions(
      area === "Decorate1" ? BEIYING_STRIP_MODES : BEIYING_KEYBOARD_MODES,
      t
    );
  }

  if (provider === "rongyuan") {
    const source =
      area === "Decorate1"
        ? buildRongyuanDecorateLightingUiOptions()
        : buildRongyuanLightingUiOptions();
    return source.map((item) => ({
      label: item.label,
      value: item.value,
    }));
  }

  return mapModeDefinitions(
    area === "Decorate1"
      ? buildXingshanStripModeDefinitions(hfdEquipment?.displayname)
      : XINGSHAN_KEYBOARD_MODES,
    t
  );
}

export function normalizeLightingDirection(raw: unknown): {
  isForward: boolean;
} {
  if (raw === "Backward" || raw === 1 || raw === "1") {
    return { isForward: false };
  }
  return { isForward: true };
}

/** 华奋达：与 LightingOperationHfd.shouldHideDirection 对齐 */
export function shouldShowHuafendaDirection(
  mode: number,
  isLightStrip: boolean
): boolean {
  if (isLightStrip) return false;
  return ![9999, 1, 2, 3, 4, 5, 6, 7, 8, 13, 14, 15, 17, 19, 20, 254].includes(
    mode
  );
}

export function shouldShowXingshanStaticColor(
  solutionProvider: string,
  area: "Keyboard" | "Decorate1"
): boolean {
  return solutionProvider.toLowerCase() === "xingshan" && area === "Keyboard";
}

export function shouldShowLightingDirection(
  solutionProvider: string,
  mode: number,
  area: "Keyboard" | "Decorate1"
): boolean {
  const provider = solutionProvider.toLowerCase();
  if (provider === "huafenda") {
    return shouldShowHuafendaDirection(mode, area === "Decorate1");
  }
  if (provider === "beiying") {
    return false;
  }
  return true;
}

export function resolvePreviewSliderValue(
  solutionProvider: string,
  value: number,
  area: "Keyboard" | "Decorate1" = "Keyboard"
): number {
  if (solutionProvider.toLowerCase() === "huafenda") {
    if (area === "Decorate1") {
      if (value >= 0 && value <= 5) return value;
      return Math.min(5, Math.max(0, Math.round(value / 20)));
    }
    if (value >= 1 && value <= 5) return value;
    return Math.min(5, Math.max(1, Math.round(value / 20) || 1));
  }
  return Math.min(100, Math.max(0, value));
}

export function getPreviewSliderRange(
  solutionProvider: string,
  area: "Keyboard" | "Decorate1" = "Keyboard"
): {
  min: number;
  max: number;
  unit?: string;
} {
  if (solutionProvider.toLowerCase() === "huafenda") {
    if (area === "Decorate1") {
      return { min: 0, max: 5 };
    }
    return { min: 1, max: 5 };
  }
  return { min: 0, max: 100, unit: "%" };
}

/** 华丰达灯带：与子应用 LightingOperationHfd.shouldHideSpeed 对齐 */
export function shouldHideHuafendaStripSpeed(mode: number): boolean {
  return [2, 5].includes(mode);
}

/** 华丰达灯带：与子应用 LightingOperationHfd.shouldHidePalette 对齐 */
export function shouldHideHuafendaStripPalette(mode: number): boolean {
  return ![2, 3].includes(mode);
}

/** 不支持「灯带」Tab 的华丰达/北鹰/星闪机型（solution-displayname）
 *  battle68 无灯带；后续华奋达磁轴可能支持，勿用 magneticAxis 整类隐藏
 *  GIF 灯效与灯带独立：X87 V2 同时支持两者 */
const NO_LIGHT_STRIP_TAB_KEYS = new Set([
  "huafenda-battle68",
  "huafenda-Tank68 HE",
  "huafenda-X75 Pro",
  "huafenda-X75 Pro Screen",
  "huafenda-M98",
  "huafenda-M98 Screen",
  "huafenda-DK108",
  "huafenda-SMART875",
  "huafenda-X98 (1K)",
  "huafenda-X87 Neon",
  "beiying-X98 (1K) Touch",
  "beiying-X98 (8K) Touch",
  "beiying-MUSE M80",
  "beiying-T87",
  "beiying-X75V2",
  "xingshan-DK63",
  "xingshan-DK63 V2",
  "xingshan-DK68 Pro",
  "xingshan-DK68-Pro",
  "xingshan-X87 HE",
]);

/** 华奋达灯箱虚拟灯带颗数（社区无 decorate1 矩阵时的默认列数） */
export const HUAFENDA_STRIP_PREVIEW_LED_COUNT = 8;

/** 与 huafendaLightingBoxData「关闭」一致 */
const HFD_LIGHT_BOX_OFF_MODE = 5;

const HFD_STRIP_PREVIEW_OFF_LED: CustomLightingInfo = {
  R: 42,
  G: 42,
  B: 42,
  isCustom: false,
};

/** 板载 decorate1Base → 社区只读灯带条（无逐灯矩阵时） */
export function buildHuafendaStripPreviewFromBase(
  params: Pick<LightingPreviewParams, "lightOpen" | "mode" | "primaryColor">,
  cols: number
): CustomLightingData {
  const count = Math.max(1, cols);
  const off =
    !params.lightOpen || Number(params.mode) === HFD_LIGHT_BOX_OFF_MODE;
  const src = off ? HFD_STRIP_PREVIEW_OFF_LED : params.primaryColor;
  const cell: CustomLightingInfo = {
    R: src.R,
    G: src.G,
    B: src.B,
    isCustom: false,
  };
  return [Array.from({ length: count }, () => ({ ...cell }))];
}

/** 板载 decorate1Base → 社区只读灯带条（X87S 等无 decorate1 矩阵时） */
export function buildRongyuanStripPreviewFromBase(
  params: Pick<LightingPreviewParams, "lightOpen" | "mode" | "primaryColor">,
  cols: number
): CustomLightingData {
  const count = Math.max(1, cols);
  const off = !params.lightOpen || isRongyuanDecorateOffUiMode(params.mode);
  const src = off ? HFD_STRIP_PREVIEW_OFF_LED : params.primaryColor;
  const fallback = RONGYUAN_DECORATE_AP_RGB;
  const cell: CustomLightingInfo = {
    R: off ? src.R : (src.R ?? fallback.R),
    G: off ? src.G : (src.G ?? fallback.G),
    B: off ? src.B : (src.B ?? fallback.B),
    isCustom: false,
  };
  return [Array.from({ length: count }, () => ({ ...cell }))];
}

export function shouldShowLightStripTab(meta: {
  displayname: string;
  solutionProvider: string;
  keyboardType?: string;
}): boolean {
  const provider = meta.solutionProvider.toLowerCase();
  if (provider === "rongyuan") {
    return supportsRongyuanDecorate1Lighting(meta.keyboardType);
  }
  const stripKey = `${provider}-${meta.displayname.trim()}`;
  if (NO_LIGHT_STRIP_TAB_KEYS.has(stripKey)) return false;
  const kt = String(meta.keyboardType ?? "")
    .trim()
    .toLowerCase();
  if (kt === "dk108" || kt === "m71" || kt === "m75") return false;
  return true;
}

/** 社区灯带预览列数：与灯光页 getXingshanDecorativeStripCols 对齐 */
export function resolveCommunityStripPreviewCols(
  meta: Pick<
    CommunityPreviewDeviceMeta,
    "solutionProvider" | "displayname" | "keyboardType"
  >,
  decorateData?: CustomLightingData | null
): number {
  const provider = meta.solutionProvider?.toLowerCase() ?? "";
  if (provider === "huafenda") {
    const dataLen = decorateData?.[0]?.length ?? 0;
    return dataLen > 0 ? dataLen : HUAFENDA_STRIP_PREVIEW_LED_COUNT;
  }
  if (provider === "rongyuan") {
    const dataLen = decorateData?.[0]?.length ?? 0;
    return dataLen > 0 ? dataLen : RONGYUAN_DECORATE_STRIP_LED_COUNT;
  }
  if (provider !== "xingshan") {
    const dataLen = decorateData?.[0]?.length ?? 0;
    return dataLen > 0 ? dataLen : HUAFENDA_STRIP_PREVIEW_LED_COUNT;
  }

  return getXingshanDecorativeStripCols(
    {
      solutionRovider: meta.solutionProvider,
      keyboardType: meta.keyboardType,
      displayname: meta.displayname,
    },
    decorateData?.[0]
  );
}

/** 按预览列数裁剪灯带矩阵 */
export function sliceCommunityStripLightingData(
  decorateData: CustomLightingData | null | undefined,
  cols: number
): CustomLightingData | undefined {
  if (!decorateData?.length || cols <= 0) return undefined;
  return decorateData.map((row) => row.slice(0, cols));
}

export function resolveKeyboardLightingTabs(meta: {
  displayname: string;
  solutionProvider: string;
  keyboardType?: string;
}): Array<"backlight" | "strip"> {
  const tabs: Array<"backlight" | "strip"> = ["backlight"];
  if (shouldShowLightStripTab(meta)) {
    tabs.push("strip");
  }
  return tabs;
}
